"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";

import { AdSlot } from "./ad-slot";
import { useAdRuntime } from "./ad-provider";
import { PopUpOverlay } from "./popup-overlay";
import { SKIN_DISMISSED_EVENT } from "./public-api";
import type { GptRenderEndedEvent } from "@/lib/ads/gpt";
import { gpt } from "@/lib/ads/gpt";

/**
 * The page-placed family. Skinner and PopUp are regular slots whose creative the page
 * positions (an out-of-page slot only receives "Out-of-page" creatives, so the legacy
 * image skins and pop-ups could never serve into one); Crazy stays out-of-page.
 *
 *   <OutOfPageSlots />   → Skinner (desktop wallpaper), PopUp (light box), Crazy — home + listing pages only
 *   <BottomBanner />     → sticky footer on phone/tablet, dismissible, 10-second rotation on direct-sold
 */
export function OutOfPageSlots() {
  const { targeting } = useAdRuntime();
  const pathname = usePathname() ?? "/";
  if (targeting.page !== "home" && targeting.page !== "listing") return null;
  return (
    <>
      <SkinnerSlot key={`skin:${pathname}`} />
      <PopUpOverlay key={`popup:${pathname}`} />
      <AdSlot unit="Crazy" className="hkej-ad-oop" />
    </>
  );
}

/** Outer share of a wallpaper that is the design gutter (1920 → 240px). */
const SKIN_GUTTER_RATIO = 0.125;
/**
 * A centred caption is short and much wider than the gutter. Fitting that width
 * into the rail shrinks the type to a few pixels. Render the block at least this
 * tall and crop the sides, so the middle of the label stays readable.
 */
const SKIN_LABEL_MIN_H = 150;

type SkinBox = { x: number; y: number; w: number; h: number };
type SkinArt = { src: string; href: string; w: number; h: number; box: SkinBox | null };

/** Friendly image creatives keep the file and the click URL in the slot iframe. */
function readSkinArt(root: HTMLElement): { src: string; href: string } | null {
  const frame = root.querySelector("iframe");
  if (!frame) return null;
  try {
    const doc = frame.contentDocument;
    const img = doc?.querySelector("img");
    const src = img?.currentSrc || img?.src;
    if (!src) return null;
    const a = img?.closest("a") ?? doc?.querySelector("a");
    return { src, href: a?.href || "" };
  } catch {
    return null;
  }
}

/**
 * A 1920 wallpaper keeps its spec in the outer 12.5% (edge mode). A 1600 or 1300
 * test file centres the spec, which the column then covers, so the rails look
 * blank. When the edges are empty, return that centre box so each rail can show it.
 */
function findCentreLabel(src: string, imgW: number, imgH: number): Promise<SkinBox | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const dw = 400;
      const dh = Math.max(1, Math.round((img.naturalHeight || imgH) * dw / (img.naturalWidth || imgW)));
      const canvas = document.createElement("canvas");
      canvas.width = dw;
      canvas.height = dh;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) { resolve(null); return; }
      ctx.drawImage(img, 0, 0, dw, dh);
      const data = ctx.getImageData(0, 0, dw, dh).data;
      const gutter = Math.round(dw * SKIN_GUTTER_RATIO);
      let left = 0, right = 0, mid = 0;
      let minx = dw, miny = dh, maxx = 0, maxy = 0;
      for (let y = 2; y < dh - 2; y += 2) {
        for (let x = 2; x < dw - 2; x += 2) {
          const i = (y * dw + x) * 4;
          if (data[i] + data[i + 1] + data[i + 2] < 80) continue;
          if (x < gutter) left++;
          else if (x >= dw - gutter) right++;
          else mid++;
          if (x < minx) minx = x;
          if (y < miny) miny = y;
          if (x > maxx) maxx = x;
          if (y > maxy) maxy = y;
        }
      }
      const total = left + right + mid;
      // Edge artwork (the 1920 file) keeps a real label in both rails. A centred
      // caption only clips the rail by a few pixels and must not count as that.
      const edges = total > 0 && left > 15 && right > 15 && (left + right) / total > 0.2;
      if (edges || maxx <= minx) { resolve(null); return; }
      const sx = (img.naturalWidth || imgW) / dw;
      const sy = (img.naturalHeight || imgH) / dh;
      resolve({
        x: Math.round(minx * sx),
        y: Math.round(miny * sy),
        w: Math.max(1, Math.round((maxx - minx) * sx)),
        h: Math.max(1, Math.round((maxy - miny) * sy)),
      });
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/**
 * Skin host. A direct child of <body> (portal) so the existing skin-mode CSS
 * (`body:has(> [data-skin]) .hkej-page-shell`, globals.css) boxes the column above it.
 *
 * - image creative (width ≥ 1000): the outer 12.5% is scaled into the real gutter and
 *   anchored to each edge, under the top banner so the banner's sides stay clear.
 *   A file whose edges are empty (the centred 1600 / 1300 test labels) shows that
 *   label in each rail. A wide caption is cropped to the rail and kept tall
 *   enough to read, instead of being shrunk to the rail width.
 * - 1x1 custom creative: it draws its own `[data-skin]` gutters; the host stays empty.
 * - `dismissSkin()` / `dismiss_skinner_Ad()` clears both.
 */
function SkinnerSlot() {
  const rt = useAdRuntime();
  const hostRef = React.useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = React.useState(false);
  const [wall, setWall] = React.useState<{ w: number; h: number } | null>(null);
  const [art, setArt] = React.useState<SkinArt | null>(null);
  const [rails, setRails] = React.useState({ left: 0, right: 0, top: 0, viewH: 0 });
  React.useEffect(() => setMounted(true), []);
  // design review: `?adpreview=1` boxes the column behind a 1920x1080 dashed wallpaper on desktop
  React.useEffect(() => {
    if (rt.preview && rt.device === "desktop") setWall({ w: 1920, h: 1080 });
  }, [rt.preview, rt.device]);
  React.useEffect(() => {
    const off = () => { setWall(null); setArt(null); };
    window.addEventListener(SKIN_DISMISSED_EVENT, off);
    return () => window.removeEventListener(SKIN_DISMISSED_EVENT, off);
  }, []);
  const onRender = React.useCallback((e: GptRenderEndedEvent) => {
    const z = e.size;
    const w = Array.isArray(z) ? Number(z[0]) : 0, h = Array.isArray(z) ? Number(z[1]) : 0;
    setWall(!e.isEmpty && w >= 1000 && h > 0 ? { w, h } : null);
    if (e.isEmpty) setArt(null);
  }, []);
  React.useEffect(() => {
    if (!wall) return;
    const root = hostRef.current;
    if (!root) return;
    let n = 0;
    let timer = 0;
    const tick = () => {
      const found = readSkinArt(root);
      if (found) {
        setArt({ ...found, w: wall.w, h: wall.h, box: null });
        void findCentreLabel(found.src, wall.w, wall.h).then((box) => {
          setArt((prev) => (prev && prev.src === found.src ? { ...prev, box } : prev));
        });
        return;
      }
      if (n++ < 40) timer = window.setTimeout(tick, 200);
    };
    tick();
    return () => window.clearTimeout(timer);
  }, [wall]);
  React.useEffect(() => {
    if (!art) return;
    const measure = () => {
      const shell = document.querySelector(".hkej-page-shell");
      const r = shell?.getBoundingClientRect();
      const next = {
        left: r ? Math.max(0, Math.round(r.left)) : 0,
        right: r ? Math.max(0, Math.round(window.innerWidth - r.right)) : 0,
        // The artwork starts at the top of the viewport. Offsetting it to the
        // banner's bottom made the wallpaper appear to begin at the navbar.
        top: 0,
        viewH: window.innerHeight,
      };
      setRails((prev) => (prev.left === next.left && prev.right === next.right && prev.top === next.top && prev.viewH === next.viewH ? prev : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    const shell = document.querySelector(".hkej-page-shell");
    if (shell) ro.observe(shell);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [art]);
  if (!mounted) return null;
  // Until the file URL is readable, keep the iframe scaled to the viewport so the
  // rails are not a bare gap. Centering a wider file would crop the gutter label.
  const k = wall && rt.device === "desktop" && typeof window !== "undefined" && window.innerWidth > 0 ? window.innerWidth / wall.w : 1;
  const style: React.CSSProperties = art
    ? { position: "fixed", top: 0, left: 0, width: wall?.w, height: wall?.h, zIndex: 0, opacity: 0, pointerEvents: "none", overflow: "hidden" }
    : wall
      ? { position: "fixed", top: 0, left: "50%", transform: `translateX(-50%) scale(${k})`, transformOrigin: "top center", width: wall.w, height: wall.h, zIndex: 0, overflow: "hidden" }
      : { position: "fixed", top: 0, left: 0, width: 0, height: 0, zIndex: 0, overflow: "visible" };
  const design = art ? Math.max(1, Math.round(art.w * SKIN_GUTTER_RATIO)) : 0;
  const edgeFit = art && rails.left > 0 ? Math.min(1, rails.left / design) : 0;
  const box = art?.box ?? null;
  const labelScale = box && rails.left > 4 && box.w > 0 && box.h > 0
    ? Math.min(1, Math.max((rails.left - 8) / box.w, SKIN_LABEL_MIN_H / box.h))
    : 0;
  const bgW = art ? (labelScale > 0 ? Math.round(art.w * labelScale) : edgeFit > 0 ? Math.round(art.w * edgeFit) : 0) : 0;
  const panel = (side: "left" | "right", width: number): React.CSSProperties => {
    // A centred caption is one short block on a tall black file. One copy
    // sat at the top of the gutter, under the masthead, and the rest of the
    // rail was black — refreshing looked like no wallpaper. Paint it again
    // further down the rail so both edges stay labelled.
    let position = `${side} 0 top ${rails.top}px`;
    let image = art ? `url("${art.src}")` : undefined;
    let size = `${bgW}px auto`;
    // The spec in an edge file (1920) sits at the top of the artwork, beside
    // the black ticker, and the rail below it is empty black. A second copy
    // sits beside the page content.
    if (art && bgW > 0 && !(box && labelScale > 0)) {
      const y2 = Math.max(240, Math.round((rails.viewH || 900) * 0.4));
      position = `${side} 0 top ${rails.top}px, ${side} 0 top ${y2}px`;
      image = `url("${art.src}"), url("${art.src}")`;
      size = `${bgW}px auto, ${bgW}px auto`;
    }
    if (box && labelScale > 0 && art) {
      const x = Math.round(width / 2 - (box.x + box.w / 2) * labelScale);
      const y1 = Math.round(rails.top + 24 - box.y * labelScale);
      const gap = Math.max(Math.round(box.h * labelScale + 36), Math.round((rails.viewH || 800) * 0.42));
      const y2 = y1 + gap;
      position = `${x}px ${y1}px, ${x}px ${y2}px`;
      image = `url("${art.src}"), url("${art.src}")`;
      size = `${bgW}px auto, ${bgW}px auto`;
    }
    return {
      position: "fixed",
      top: 0,
      bottom: 0,
      [side]: 0,
      width,
      zIndex: 0,
      pointerEvents: "auto",
      backgroundColor: "#000",
      backgroundImage: image,
      backgroundRepeat: "no-repeat",
      backgroundSize: size,
      backgroundPosition: position,
    };
  };
  return createPortal(
    <>
      <div ref={hostRef} data-ad-host="Skinner" data-ad-container="Skinner" data-skin={wall ? "image" : undefined} style={style}>
        <AdSlot unit="Skinner" onRender={onRender} className="hkej-ad-skin" style={wall ? { width: wall.w, height: wall.h } : undefined} />
      </div>
      {art && bgW > 0 && rails.left > 0 && (
        <div data-ad-host="SkinnerRails" style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" }}>
          <a href={art.href || undefined} target="_blank" rel="noopener noreferrer" style={panel("left", rails.left)} />
          <a href={art.href || undefined} target="_blank" rel="noopener noreferrer" style={panel("right", rails.right || rails.left)} />
        </div>
      )}
    </>,
    document.body,
  );
}

const ROTATE_MS = 10_000;

const PEEK_PX = 16;

export function BottomBanner({ label = "廣告" }: { label?: string }) {
  const pathname = usePathname() ?? "";
  const rt = useAdRuntime();
  const [collapsed, setCollapsed] = React.useState(false);
  const [phase, setPhase] = React.useState<"pending" | "on" | "off">("pending");
  const slotRef = React.useRef<GptRenderEndedEvent["slot"] | null>(null);
  const timer = React.useRef<number | null>(null);

  const onRender = React.useCallback((e: GptRenderEndedEvent) => {
    slotRef.current = e.slot;
    setPhase(e.isEmpty ? "off" : "on");
    if (timer.current) window.clearInterval(timer.current);
    // Rate card: "Bottom Banners rotate every 10 seconds among advertisers".
    // Only for direct-sold fills — Ad Exchange requires a declared refresh ≥30 s.
    if (!e.isEmpty && e.isBackfill === false) {
      timer.current = window.setInterval(() => {
        if (slotRef.current) gpt((g) => g.pubads().refresh([slotRef.current!]));
      }, ROTATE_MS);
    }
  }, []);

  React.useEffect(() => () => { if (timer.current) window.clearInterval(timer.current); }, []);

  // A 0-height clip kept the slot out of view, so GPT never painted and the bar
  // stayed pending. The slot keeps its real height until the response is empty.
  // Hiding slides the bar down and leaves a sliver; it does not unmount the slot.
  // 功能比較 has no bottom bar. The slot is not requested on that page.
  if (/\/stock360\/upgrade\/?$/.test(pathname)) return null;
  if (phase === "off") return null;
  const shown = !rt.enabled || phase === "on";

  return (
    <div
      className="hkej-ad-bottom lg:hidden"
      data-ad-container="BottomBanner"
      data-ad-label={label}
      data-ad-collapsed={collapsed ? "" : undefined}
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 60,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        background: "var(--hkej-ad-bottom, var(--hkej-ad-band, #f7f7f7))",
        // LJ clears this. Other brands keep the hairline along the top of the bar.
        boxShadow: "var(--hkej-ad-bottom-edge, 0 -1px 0 rgba(0, 0, 0, 0.08))",
        paddingBottom: "env(safe-area-inset-bottom)",
        transform: collapsed ? `translateY(calc(100% - ${PEEK_PX}px))` : "translateY(0)",
        transition: "transform 280ms ease",
        lineHeight: 0,
      }}
    >
      <style>{`.hkej-ad-bottom .hkej-ad{justify-content:flex-start!important}.hkej-ad-bottom .hkej-ad iframe{border:0!important;display:block}`}</style>
      {shown && (
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          aria-expanded={!collapsed}
          aria-label={collapsed ? "展開廣告" : "隱藏廣告"}
          style={{
            position: "absolute",
            // 1px overlap: the button's border box and the creative were
            // leaving a hairline of the page between them.
            top: -21,
            right: 8,
            height: 22,
            padding: "0 8px",
            fontSize: 11,
            lineHeight: "22px",
            background: "var(--hkej-ad-bottom, var(--hkej-ad-band, #f7f7f7))",
            color: "var(--hkej-ad-label, #767676)",
            border: "1px solid rgba(0,0,0,0.08)",
            borderBottom: 0,
            borderRadius: "4px 4px 0 0",
            cursor: "pointer",
          }}
        >
          {collapsed ? "展開" : "隱藏"}
        </button>
      )}
      <AdSlot unit="BottomBanner" onRender={onRender} />
    </div>
  );
}
